import React, {useEffect, useState} from 'react';
import {Input, Form, Select, Radio} from 'antd';
import {CaretDownOutlined} from '@ant-design/icons';
import './style.css';
import {OnlyAlphabetInputValidatorWithSpecialCharacters} from '../../../Util/commonUtility';
import {validateCompanyName} from '../../../Util/commonUtility';
import {AttendanceType} from '../../../Util/commonUtility';
import {isInPersonCapacityFull} from '../../../Util/commonUtility';
import useLocalization from '../../../Localization';
import ViewportSelect from '../ViewportPopup/ViewportSelect';

const {Option} = Select;
const JAPAN_LOCAL_CHAPTER_NAME = 'japan';

export const resetAttendanceDependentOptions = (form, isWebcast) => {
  if (!form) return;
  form.setFields([
    {
      name: 'registration_option',
      value: undefined,
      errors: [],
      touched: false,
    },
    {
      name: 'food_option',
      value: isWebcast ? 'no-food' : undefined,
      errors: [],
      touched: false,
    },
  ]);
};

const EditableCell = ({
  editing,
  dataIndex,
  title,
  eventType,
  isMeal,
  record,
  index,
  children,
  allEventOptionsFoodData,
  allEventOptionsRegistrationData,
  attendeesData = [],
  eventLocalChapterData,
  usedForSponsorDetails = false,
  usedForSponsorPerks = false,
  freeLunch,
  workshopAttendee,
  transactionId,
  checkedAttendeeInPerson,
  handleAttendeeInPersonChange,
  eventISOnline,
  inPersonRemaining,
  ...restProps
}) => {
  let inputElement;
  const {ApplicationString} = useLocalization();

  const filterEmptyAttendeeData = attendeesData?.filter(
    (obj) => obj.id !== '1',
  );

  // Registration and food dropdowns stay disabled until an attendance
  // type is chosen. Options tagged BOTH are always shown after that, plus
  // the matching In-Person or Webcast options. Untagged options are BOTH.
  const selectedAttendanceType =
    checkedAttendeeInPerson === true
      ? AttendanceType.IN_PERSON
      : checkedAttendeeInPerson === false
      ? AttendanceType.WEBCAST
      : null;
  const filteredRegistrationOptions = allEventOptionsRegistrationData?.filter(
    (regOption) => {
      const effectiveType = regOption?.attendance_type || AttendanceType.BOTH;
      if (effectiveType === AttendanceType.BOTH) return true;
      return effectiveType === selectedAttendanceType;
    },
  );
  const isJapanLocalChapter =
    eventLocalChapterData?.name?.toLowerCase() === JAPAN_LOCAL_CHAPTER_NAME;

  const [isActive, setActive] = useState(true);
  const toggleClass = () => {
    setActive(!isActive);
  };
  const uniqueEmailValidator = (value, record) => {
    const trimmedAttendeeEmail = record?.attendee_email?.toLowerCase()?.trim();
    const trimmedValue = value?.toLowerCase()?.trim();
    if (trimmedAttendeeEmail !== trimmedValue && attendeesData?.length > 0) {
      const sameEmail = attendeesData?.find(
        (obj) => obj?.attendee_email?.toLowerCase()?.trim() === trimmedValue,
      );
      if (sameEmail) {
        return Promise.reject(
          new Error(
            `${value} ${ApplicationString['editableCell.emailAlreadyExists']}`,
          ),
        );
      }
    }
    return Promise.resolve();
  };

  const inPersonCapacityFull = isInPersonCapacityFull(inPersonRemaining);
  const optionShowsInPersonFull = (option) => {
    if (!inPersonCapacityFull) {
      return false;
    }
    const attendanceType = option?.attendance_type || AttendanceType.BOTH;
    if (attendanceType === AttendanceType.IN_PERSON) {
      return true;
    }
    return (
      attendanceType === AttendanceType.BOTH && checkedAttendeeInPerson === true
    );
  };

  const renderOptionLabel = (option, isRegistrationOption = false) => {
    const totalRemainingQuantity = option?.qty - option?.used_qty;
    if (
      totalRemainingQuantity <= 0 ||
      (isRegistrationOption && optionShowsInPersonFull(option))
    ) {
      return (
        <>
          {option.label}
          <br />
          <span
            className='quantity-exhausted-message'
            style={{
              color: '#FF0000',
              fontSize: '12px',
              fontWeight: 'semibold',
            }}>
            {ApplicationString['editableCell.waitingList']}
          </span>
        </>
      );
    }
    return <>{option.label}</>;
  };

  const disableOption = (option, isLunchOption = false) => {
    const totalRemainingQuantity = option?.qty - option?.used_qty;
    const allAttendeeFilterData = attendeesData?.filter(
      (obj) =>
        obj?.event_registration_options?.[
          isLunchOption ? 'food' : 'registration'
        ]?.event_option_id === option.id,
    );

    if (
      isLunchOption &&
      (totalRemainingQuantity === 0 ||
        allAttendeeFilterData?.length >= totalRemainingQuantity)
    ) {
      return {
        value: true,
        message: ApplicationString['editableCell.quantityExhausted'],
      };
    }

    if (
      option?.limit &&
      option?.limit > 0 &&
      allAttendeeFilterData?.length >= option?.limit
    ) {
      return {
        value: true,
        message: ApplicationString['editableCell.limitExhausted'],
      };
    }

    if (
      !isLunchOption &&
      (totalRemainingQuantity === 0 ||
        allAttendeeFilterData?.length >= totalRemainingQuantity) &&
      filterEmptyAttendeeData?.length > 0
    ) {
      return {
        value: true,
        message: ApplicationString['editableCell.quantityExhausted'],
      };
    }

    return {value: false, message: ''};
  };

  const disableLunchOptions = (foodOption) => disableOption(foodOption, true);
  const disableRegisterOptions = (regOption) => disableOption(regOption);
  const isWebcastSelected = checkedAttendeeInPerson === false;
  const isAttendanceTypeChosen =
    checkedAttendeeInPerson === true || checkedAttendeeInPerson === false;
  const shouldDisableAttendanceOptions =
    typeof handleAttendeeInPersonChange === 'function' &&
    !isAttendanceTypeChosen;
  const form = Form.useFormInstance();
  const noFoodOptions = (allEventOptionsFoodData || []).filter(
    (foodOption) => foodOption?.value === 'no-food',
  );
  const foodOptionsForAttendance = isWebcastSelected
    ? noFoodOptions.length
      ? noFoodOptions
      : [
          {
            value: 'no-food',
            label:
              ApplicationString['addAttendeesComponent.label.noFoodOption'],
            price: 0,
          },
        ]
    : allEventOptionsFoodData;

  useEffect(() => {
    if (editing && dataIndex === 'option' && isWebcastSelected) {
      form.setFields([
        {
          name: 'food_option',
          value: 'no-food',
          errors: [],
          touched: false,
        },
      ]);
    }
  }, [editing, dataIndex, isWebcastSelected, form]);

  const onAttendanceTypeChange = (e) => {
    const isWebcast = e.target.value === 'webcast';
    resetAttendanceDependentOptions(form, isWebcast);
    handleAttendeeInPersonChange?.(e);
  };

  switch (dataIndex) {
    case 'name':
      inputElement = (
        <div className='field-separation'>
          <Form.Item
            name='attendee_first_name'
            className='no-margin'
            rules={[
              {
                required: true,
                message:
                  ApplicationString['editableCell.pleaseFillOutFirstName'],
              },
              {
                validator: (_, value) =>
                  OnlyAlphabetInputValidatorWithSpecialCharacters(
                    value,
                    ApplicationString['editableCell.firstName'],
                  ),
              },
            ]}>
            <Input
              id='attendee_first_name'
              placeholder={
                ApplicationString['editableCell.enterAttendeeFirstName']
              }
            />
          </Form.Item>
          <Form.Item
            name='attendee_last_name'
            className='no-margin'
            rules={[
              {
                required: true,
                message:
                  ApplicationString['editableCell.pleaseFillOutLastName'],
              },
              {
                validator: (_, value) =>
                  OnlyAlphabetInputValidatorWithSpecialCharacters(
                    value,
                    ApplicationString['editableCell.lastName'],
                  ),
              },
            ]}>
            <Input
              id='attendee_last_name'
              placeholder={
                ApplicationString['editableCell.enterAttendeeLastName']
              }
            />
          </Form.Item>
        </div>
      );
      break;
    case 'registration_type':
      inputElement = (
        <Form.Item name={dataIndex} style={{margin: 0, width: '100%'}}>
          <div className='radio-editableCell'>
            <Radio.Group
              onChange={onAttendanceTypeChange}
              value={
                checkedAttendeeInPerson === true
                  ? 'in-person'
                  : checkedAttendeeInPerson === false
                  ? 'webcast'
                  : undefined
              }>
              <Radio value='in-person'>
                {ApplicationString['editableCell.inPerson']}
                {inPersonCapacityFull && (
                  <div
                    className='quantity-exhausted-message'
                    style={{
                      color: '#FF0000',
                      fontSize: '12px',
                      fontWeight: 'semibold',
                    }}>
                    {ApplicationString['editableCell.waitingList']}
                  </div>
                )}
              </Radio>
              <Radio value='webcast' className='webcast-registration-type'>
                {ApplicationString['editableCell.webcast']}
                <div className='forNonLocalsOnly-registration-type'>
                  {ApplicationString['editableCell.forNonLocalsOnly']}
                </div>
              </Radio>
            </Radio.Group>
          </div>
        </Form.Item>
      );
      break;
    case 'attendee_email':
      inputElement = (
        <div className='field-separation'>
          <Form.Item
            name={dataIndex}
            className='no-margin'
            rules={[
              {
                required: true,
                message: ApplicationString['editableCell.pleaseFillOut'](
                  title?.toLowerCase(),
                ),
              },
              {
                type: 'email',
                message:
                  ApplicationString['editableCell.pleaseEnterValidEmail'],
              },
              {
                validator: (_, value) => uniqueEmailValidator(value, record),
              },
            ]}>
            <Input
              id={dataIndex}
              placeholder={`${
                usedForSponsorDetails
                  ? ApplicationString['editableCell.enter'](
                      title?.toLowerCase(),
                    )
                  : ApplicationString['editableCell.enterAttendee'](
                      title?.toLowerCase(),
                    )
              }`}
            />
          </Form.Item>
          <Form.Item
            name={`${dataIndex}_confirm`}
            className='no-margin'
            rules={[
              {
                required: true,
                message: ApplicationString['editableCell.pleaseConfirm'](
                  title?.toLowerCase(),
                ),
              },
              ({getFieldValue}) => ({
                validator(_, value) {
                  if (
                    !value ||
                    getFieldValue(dataIndex)?.toLowerCase() ===
                      value?.toLowerCase()
                  ) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(
                      ApplicationString['editableCell.emailIdsMustMatch'],
                    ),
                  );
                },
              }),
            ]}>
            <Input
              id={`${dataIndex}_confirm`}
              onCopy={(e) => e.preventDefault()} // Disable copy
              onPaste={(e) => e.preventDefault()} // Disable paste
              placeholder={`${
                usedForSponsorDetails
                  ? ApplicationString['editableCell.confirm'](
                      title?.toLowerCase(),
                    )
                  : ApplicationString['editableCell.confirmAttendee'](
                      title?.toLowerCase(),
                    )
              }
              `}
            />
          </Form.Item>
        </div>
      );
      break;
    case 'attendee_company':
      inputElement = (
        <div className='field-separation'>
          <Form.Item
            name={dataIndex}
            rules={[
              {
                required: true,
                message: ApplicationString['editableCell.pleaseFillOut'](
                  title?.toLowerCase(),
                ),
              },
              {
                validator: (_, value) => validateCompanyName(value, title),
              },
            ]}
            style={{margin: 0}}>
            <Input
              id={dataIndex}
              placeholder={
                ApplicationString['editableCell.enterCompanyAffiliation']
              }
            />
          </Form.Item>
          {isJapanLocalChapter && (
            <>
              <Form.Item
                name='attendee_department'
                style={{margin: 0}}
                validateTrigger={['onChange', 'onBlur']}
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: ApplicationString['editableCell.pleaseFillOut'](
                      ApplicationString[
                        'editableCell.departmentDivision'
                      ]?.toLowerCase(),
                    ),
                  },
                ]}>
                <Input
                  id='attendee_department'
                  placeholder={
                    ApplicationString[
                      'editableCell.enterAttendeeDepartmentDivision'
                    ]
                  }
                />
              </Form.Item>

              <Form.Item
                name='attendee_job_title'
                style={{margin: 0}}
                validateTrigger={['onChange', 'onBlur']}
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: ApplicationString['editableCell.pleaseFillOut'](
                      ApplicationString[
                        'editableCell.jobTitleRole'
                      ]?.toLowerCase(),
                    ),
                  },
                ]}>
                <Input
                  id='attendee_job_title'
                  placeholder={
                    ApplicationString['editableCell.enterAttendeeJobTitleRole']
                  }
                />
              </Form.Item>
            </>
          )}
        </div>
      );
      break;
    case 'option':
      inputElement = (
        <div className='field-separation'>
          <Form.Item
            name='registration_option'
            className='no-margin'
            allowClear
            rules={[
              {
                required: true,
                message:
                  ApplicationString[
                    'editableCell.pleaseSelectRegistrationOption'
                  ],
              },
            ]}>
            <ViewportSelect
              id='registration_option'
              key={`registration-option-${selectedAttendanceType || 'none'}`}
              showSearch
              allowClear
              virtual={false}
              optionFilterProp='label'
              popupMatchSelectWidth={false}
              disabled={shouldDisableAttendanceOptions}
              dropdownStyle={{
                width: '620px',
              }}
              filterOption={(input, option) =>
                String(option?.label ?? '')
                  .toLowerCase()
                  .includes(String(input ?? '').toLowerCase())
              }
              placeholder={
                ApplicationString[
                  'editableCell.selectAttendeeRegistrationOption'
                ]
              }
              className='editable-options'
              popupClassName='editable-options-attendee-dropdown'
              suffixIcon={
                <CaretDownOutlined
                  className={isActive ? 'ant-select-suffix' : ''}
                  onClick={toggleClass}
                />
              }>
              {filteredRegistrationOptions?.map((regOption) => (
                <Option
                  key={regOption.value}
                  value={regOption.value}
                  label={regOption.label}
                  disabled={disableRegisterOptions(regOption).value}>
                  {disableRegisterOptions(regOption).value ? (
                    <>
                      {regOption.label}
                      <br />
                      {disableRegisterOptions(regOption).message}
                    </>
                  ) : (
                    <>{renderOptionLabel(regOption, true)}</>
                  )}
                </Option>
              ))}
            </ViewportSelect>
          </Form.Item>
          {
            (inputElement = (eventType?.toLowerCase() !== 'webcast' ||
              !eventISOnline) && (
              <Form.Item
                name='food_option'
                className='no-margin'
                rules={[
                  {
                    required: true,
                    message:
                      ApplicationString['editableCell.pleaseSelectFoodOption'],
                  },
                ]}>
                <ViewportSelect
                  id='food_option'
                  key={`food-option-${selectedAttendanceType || 'none'}`}
                  showSearch
                  allowClear
                  virtual={false}
                  optionFilterProp='label'
                  filterOption={(input, option) =>
                    String(option?.label ?? '')
                      .toLowerCase()
                      .includes(String(input ?? '').toLowerCase())
                  }
                  placeholder={
                    ApplicationString['editableCell.selectAttendeeFoodOption']
                  }
                  disabled={
                    shouldDisableAttendanceOptions ||
                    (transactionId ? (record.food_option ? false : true) : false)
                  }
                  className='editable-options'
                  popupClassName='editable-options-attendee-dropdown'
                  suffixIcon={
                    <CaretDownOutlined
                      className={isActive ? 'ant-select-suffix' : ''}
                      onClick={toggleClass}
                    />
                  }>
                  {foodOptionsForAttendance?.map((foodOption) => (
                    <Option
                      key={foodOption.value}
                      value={foodOption.value}
                      label={foodOption.label}
                      disabled={disableLunchOptions(foodOption).value}>
                      {disableRegisterOptions(foodOption).value ? (
                        <>
                          {foodOption.label}
                          <br />
                          {disableRegisterOptions(foodOption).message}
                        </>
                      ) : (
                        <>{renderOptionLabel(foodOption)}</>
                      )}{' '}
                    </Option>
                  ))}
                </ViewportSelect>
              </Form.Item>
            ))
          }
        </div>
      );
      break;
    default:
      inputElement = (
        <Form.Item
          name={dataIndex}
          rules={[
            {
              required: true,
              message: ApplicationString['editableCell.pleaseFillOut'](
                title?.toLowerCase(),
              ),
            },
            {
              validator: (_, value) =>
                OnlyAlphabetInputValidatorWithSpecialCharacters(value, title),
            },
          ]}
          style={{margin: 0}}>
          <Input
            id={dataIndex}
            placeholder={`${
              usedForSponsorDetails
                ? ApplicationString['editableCell.enter'](title?.toLowerCase())
                : ApplicationString['editableCell.enterAttendee'](
                    title?.toLowerCase(),
                  )
            }`}
          />
        </Form.Item>
      );
  }

  const cellClassName = [
    restProps.className,
    dataIndex === 'registration_type' ? 'attendance-type-cell' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <td {...restProps} className={cellClassName}>
      {editing ? <div>{inputElement}</div> : children}
    </td>
  );
};

export default EditableCell;
